import useSWR from 'swr';
import { widgetService, isApiError, normalizeUuid } from '@dopomogai/api-client';
import type { UUID, WidgetTemplateResponse, WidgetVersionResponse } from '@dopomogai/types';
import { AgentKitError } from '../types';

interface ActiveWidgetData {
  template: WidgetTemplateResponse | null;
  activeVersion: WidgetVersionResponse | null;
  embedScript: string | null;
}

const fetchActiveWidget = async ([, agentId]: [string, UUID]): Promise<ActiveWidgetData> => {
  const versionsResponse = await widgetService.getVersionsByAgent(agentId);
  if (isApiError(versionsResponse)) {
    throw new AgentKitError(
      versionsResponse.message ?? `Failed to fetch widget versions for agent ${agentId}`,
      versionsResponse,
    );
  }

  const activeVersion = versionsResponse.find(version => version.is_active);
  if (!activeVersion) {
    return { template: null, activeVersion: null, embedScript: null };
  }

  const cleanedTemplateId = normalizeUuid(activeVersion.template_id);
  const [templateResponse, embedResponse] = await Promise.all([
    widgetService.getTemplate(cleanedTemplateId as UUID),
    widgetService.getTemplateEmbedCode(cleanedTemplateId as UUID),
  ]);

  if (isApiError(templateResponse)) {
    throw new AgentKitError(
      templateResponse.message ?? `Failed to fetch widget template ${cleanedTemplateId}`,
      templateResponse,
    );
  }

  if (isApiError(embedResponse)) {
    throw new AgentKitError(
      embedResponse.message ?? `Failed to fetch embed code for template ${cleanedTemplateId}`,
      embedResponse,
    );
  }

  return {
    template: templateResponse,
    activeVersion,
    embedScript: embedResponse.script ?? null,
  };
};

export const useActiveWidgetForAgent = (agentId: UUID | null) => {
  const cacheKey = agentId ? ['active-widget-for-agent', agentId] : null;

  const { data, error, isLoading, mutate } = useSWR<ActiveWidgetData, AgentKitError>(
    cacheKey,
    fetchActiveWidget,
  );

  return {
    template: data?.template ?? null,
    activeVersion: data?.activeVersion ?? null,
    embedScript: data?.embedScript ?? null,
    isLoading,
    error,
    mutate,
  };
};
